import type { SecretListing } from "@repo/app-types/entities/secret";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import {
  useCreatableCombobox,
  type ComboboxCreateOption,
} from "@repo/ui/combobox/useCreatableCombobox";
import { useMemo, type FC } from "react";
import { useListSecretsQuery } from "../../../api/entities/secret.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { formatTypedSecretName } from "../formatTypedSecretName.js";

/** One of the company's secrets, or the new one being created. */
interface SecretNameOption {
  id: string;
  label: string;
}

/** The key the "New" option is created under. Not a secret id: those are uuids. */
const newSecretOptionId = "new-secret";

// Module scope: the combobox hook lists these among its memo dependencies.
const getOptionLabel = (option: SecretNameOption | ComboboxCreateOption) =>
  option.label;
const formatNewSecretLabel = (name: string) => `New "${name}" secret`;

interface SecretNameSearchFieldProps {
  /** Called with an existing secret when one is chosen. */
  onSelect: (secret: SecretListing) => void;
  /** Called with the typed name when its "New" option is chosen. */
  onCreate: (name: string) => void;
  /** Secrets the search leaves out, such as those already listed. */
  excludedSecrets: readonly SecretListing[];
}

/**
 * Lists the company's secrets to pick from, filtered by the typed name, or
 * offers that name as a new one.
 */
export const SecretNameSearchField: FC<SecretNameSearchFieldProps> = (
  props,
) => {
  const { onSelect, onCreate, excludedSecrets } = props;

  const { data: secrets, isLoading, error: listError } = useListSecretsQuery();

  const options = useMemo(
    () =>
      (secrets ?? [])
        .filter(
          ({ id }) => !excludedSecrets.some((excluded) => excluded.id === id),
        )
        .map(({ id, name }) => ({ id, label: name })),
    [secrets, excludedSecrets],
  );

  // Adds the `New "NAME" secret` option unless a listed secret has the name typed.
  const { comboboxProps } = useCreatableCombobox<SecretNameOption>({
    items: options,
    getLabel: getOptionLabel,
    value: null,
    onChange: (key) => {
      const selected = secrets?.find(({ id }) => id === key);
      if (selected) {
        onSelect(selected);
      }
    },
    onCreate: (name) => {
      onCreate(name);
      return newSecretOptionId;
    },
    formatCreateLabel: formatNewSecretLabel,
  });
  const { defaultItems, ...fieldProps } = comboboxProps;

  return (
    <ComboboxField<SecretNameOption | ComboboxCreateOption>
      {...fieldProps}
      // The typed text is shaped like a secret name as it is typed, so the
      // list is filtered by, and a new secret gets, the name it will be saved as.
      onInputChange={(text) =>
        fieldProps.onInputChange(formatTypedSecretName(text))
      }
      // Nothing stays selected: choosing an option hands it on.
      value={null}
      label="Name"
      items={defaultItems}
      getLabel={getOptionLabel}
      // Never disabled: while the list loads or after it fails, a new name can
      // still be typed, and the field keeps its focus.
      placeholder={isLoading ? "Loading…" : "Search secrets or type a new name"}
      errorMessage={
        listError
          ? getApiErrorMessage(listError, "Failed to load secrets")
          : undefined
      }
      autoFocus
    />
  );
};
